import React, { useState, useEffect, useRef } from 'react';
import CoachProfileSkeleton from "../../components/layout/CoachProfileSkeleton";
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../hooks/useTheme';
import { Save, User, Camera, X, Upload, Moon, Sun, Clock } from 'lucide-react';
import api from '../../api/api';

export default function CoachProfile() {
    const { user, setUser } = useAuth();
    const { isDarkMode, setDarkMode } = useTheme();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [profilePicture, setProfilePicture] = useState(null);
    const [profilePictureSource, setProfilePictureSource] = useState(null);
    const [uploadingPicture, setUploadingPicture] = useState(false);
    const fileInputRef = useRef(null);

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        linkedinUrl: '',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    });

    // Only coaches/admins may access this page
    useEffect(() => {
        if (user && !user.isCoach && !user.isAdmin) {
            navigate('/userdashboard');
        }
    }, [user, navigate]);

    // Fetch current user data
    useEffect(() => {
        const fetchUserData = async () => {
            try {
                setLoading(true);
                const response = await api.get('/user/me');
                const userData = response.data;

                try {
                    const picResponse = await api.get('/user/profile-picture');
                    if (picResponse.data && picResponse.data.image) {
                        setProfilePicture(picResponse.data.image);
                        setProfilePictureSource(picResponse.data.source || 'uploaded');
                    }
                } catch {
                    // No profile picture found - that's okay
                }

                setFormData({
                    firstName: userData.first_name || '',
                    lastName: userData.last_name || '',
                    email: userData.email || '',
                    linkedinUrl: userData.linkedin_url || '',
                    timezone: userData.timezone || user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
                });

                if (userData.dark_mode !== undefined) {
                    setDarkMode(userData.dark_mode);
                }
            } catch (err) {
                console.error('Error fetching user data:', err);
                setError('Failed to load profile data');
            } finally {
                setLoading(false);
            }
        };

        if (user) {
            fetchUserData();
        }
    }, [user, setDarkMode]);

    const handleSave = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        try {
            setSaving(true);
            const response = await api.patch('/user/update_user', {
                first_name: formData.firstName,
                last_name: formData.lastName,
                email: formData.email,
                // Prepend the scheme users usually omit
                linkedin_url: formData.linkedinUrl.trim() && !/^https?:\/\//i.test(formData.linkedinUrl.trim())
                    ? `https://${formData.linkedinUrl.trim()}`
                    : formData.linkedinUrl.trim(),
                timezone: formData.timezone,
                DarkModeEnabled: isDarkMode
            });

            if (response.data && (response.data.success || response.data.message)) {
                setSuccess('Profile settings saved successfully!');
                setTimeout(() => setSuccess(''), 3000);
            } else {
                throw new Error('Unexpected response format');
            }
        } catch (err) {
            console.error('Error saving profile:', err);
            if (err.code === 'ECONNABORTED' || err.message === 'Network Error' || !err.response) {
                setError('Network error: Could not connect to server. Please check if the server is running.');
            } else {
                const errorMessage = err.response?.data?.error ||
                                     err.response?.data?.message ||
                                     err.message ||
                                     'Failed to save profile settings';
                setError(errorMessage);
            }
        } finally {
            setSaving(false);
        }
    };

    const handlePictureUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            setError('Invalid file type. Please upload a JPEG, PNG, GIF, or WebP image.');
            return;
        }

        const maxSize = 5 * 1024 * 1024;
        if (file.size > maxSize) {
            setError('File size exceeds 5MB limit. Please choose a smaller image.');
            return;
        }

        try {
            setUploadingPicture(true);
            setError('');

            const payload = new FormData();
            payload.append('file', file);

            const response = await api.post('/user/profile-picture', payload, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            if (response.data && response.data.image) {
                setProfilePicture(response.data.image);
                setProfilePictureSource('uploaded');
                setUser(prev => ({ ...prev, profilePicture: response.data.image }));
                setSuccess('Profile picture uploaded successfully!');
                setTimeout(() => setSuccess(''), 3000);
            }
        } catch (err) {
            console.error('Error uploading profile picture:', err);
            setError(err.response?.data?.error || 'Failed to upload profile picture');
        } finally {
            setUploadingPicture(false);
        }
    };

    const handleDeletePicture = async () => {
        if (!window.confirm('Are you sure you want to delete your profile picture?')) {
            return;
        }

        try {
            setUploadingPicture(true);
            await api.delete('/user/profile-picture');
            try {
                const picResponse = await api.get('/user/profile-picture?prefer_linkedin=true');
                if (picResponse.data && picResponse.data.image) {
                    setProfilePicture(picResponse.data.image);
                    setProfilePictureSource(picResponse.data.source || 'linkedin');
                    setUser(prev => ({ ...prev, profilePicture: picResponse.data.image }));
                } else {
                    setProfilePicture(null);
                    setProfilePictureSource(null);
                    setUser(prev => ({ ...prev, profilePicture: null }));
                }
            } catch {
                setProfilePicture(null);
                setProfilePictureSource(null);
                setUser(prev => ({ ...prev, profilePicture: null }));
            }
            setSuccess('Profile picture deleted successfully!');
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            console.error('Error deleting profile picture:', err);
            setError(err.response?.data?.error || 'Failed to delete profile picture');
        } finally {
            setUploadingPicture(false);
        }
    };

    if (loading) {
        return <CoachProfileSkeleton isDarkMode={isDarkMode} />;
    }

    return (
        <div className={`max-w-4xl mx-auto space-y-8 p-6 ${isDarkMode ? 'bg-slate-900' : ''}`}>
            <header>
                <h1 className={`text-3xl font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Profile Settings</h1>
                <p className={isDarkMode ? 'text-slate-400' : 'text-slate-600'}>
                    Manage your coaching profile and account display settings.
                </p>
            </header>

            <form onSubmit={handleSave}>
                <div className={`rounded-3xl border shadow-sm p-8 space-y-10 ${
                    isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
                }`}>
                    {/* Basic Information */}
                    <section>
                        <div className="flex items-center gap-3 mb-6">
                            <User className="text-primary-600" size={24} />
                            <h3 className={`text-xl font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Basic Information</h3>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <label className={`text-sm font-bold uppercase tracking-wide ${
                                    isDarkMode ? 'text-slate-400' : 'text-slate-500'
                                }`}>First Name</label>
                                <input
                                    type="text"
                                    value={formData.firstName}
                                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                                    className={`w-full p-3 rounded-xl border outline-none focus:ring-4 ${
                                        isDarkMode
                                            ? 'border-slate-600 bg-slate-700 text-slate-100 focus:ring-primary-500/20 focus:border-primary-500'
                                            : 'border-slate-200 bg-white focus:ring-primary-50'
                                    }`}
                                />
                            </div>
                            <div className="space-y-2">
                                <label className={`text-sm font-bold uppercase tracking-wide ${
                                    isDarkMode ? 'text-slate-400' : 'text-slate-500'
                                }`}>Last Name</label>
                                <input
                                    type="text"
                                    value={formData.lastName}
                                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                                    className={`w-full p-3 rounded-xl border outline-none focus:ring-4 ${
                                        isDarkMode
                                            ? 'border-slate-600 bg-slate-700 text-slate-100 focus:ring-primary-500/20 focus:border-primary-500'
                                            : 'border-slate-200 bg-white focus:ring-primary-50'
                                    }`}
                                />
                            </div>
                            <div className="space-y-2">
                                <label className={`text-sm font-bold uppercase tracking-wide ${
                                    isDarkMode ? 'text-slate-400' : 'text-slate-500'
                                }`}>Email</label>
                                <input
                                    type="email"
                                    // Sign-in and the reset code go by email, so it can't be saved blank
                                    required
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    className={`w-full p-3 rounded-xl border outline-none focus:ring-4 ${
                                        isDarkMode
                                            ? 'border-slate-600 bg-slate-700 text-slate-100 focus:ring-primary-500/20 focus:border-primary-500'
                                            : 'border-slate-200 bg-white focus:ring-primary-50'
                                    }`}
                                />
                            </div>
                            <div className="space-y-2">
                                <label className={`text-sm font-bold uppercase tracking-wide ${
                                    isDarkMode ? 'text-slate-400' : 'text-slate-500'
                                }`}>Timezone</label>
                                <div className={`flex items-center gap-2 w-full p-3 rounded-xl border ${
                                    isDarkMode
                                        ? 'border-slate-600 bg-slate-700 text-slate-200'
                                        : 'border-slate-200 bg-slate-50 text-slate-700'
                                }`}>
                                    <Clock size={18} className={isDarkMode ? 'text-slate-400' : 'text-slate-500'} />
                                    <span className="text-sm">{formData.timezone}</span>
                                </div>
                            </div>
                            <div className="md:col-span-2 space-y-2">
                                <label className={`text-sm font-bold uppercase tracking-wide ${
                                    isDarkMode ? 'text-slate-400' : 'text-slate-500'
                                }`}>LinkedIn URL</label>
                                <input
                                    type="text"
                                    value={formData.linkedinUrl}
                                    onChange={(e) => setFormData({ ...formData, linkedinUrl: e.target.value })}
                                    placeholder="https://www.linkedin.com/in/your-profile"
                                    className={`w-full p-3 rounded-xl border outline-none focus:ring-4 ${
                                        isDarkMode
                                            ? 'border-slate-600 bg-slate-700 text-slate-100 focus:ring-primary-500/20 focus:border-primary-500'
                                            : 'border-slate-200 bg-slate-50 focus:ring-primary-50'
                                    }`}
                                />
                            </div>
                        </div>
                    </section>

                    <hr className={isDarkMode ? 'border-slate-700' : 'border-slate-100'} />

                    {/* Profile Picture */}
                    <section>
                        <div className="flex items-center gap-3 mb-6">
                            <Camera className="text-primary-600" size={24} />
                            <h3 className={`text-xl font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Profile Picture</h3>
                        </div>

                        <div className="flex items-start gap-6">
                            <div className="relative">
                                {profilePicture ? (
                                    <div className="relative group">
                                        <img
                                            src={profilePicture}
                                            alt="Profile"
                                            className="w-32 h-32 rounded-full object-cover border-4 border-slate-200 shadow-lg"
                                        />
                                        <button
                                            type="button"
                                            onClick={handleDeletePicture}
                                            disabled={uploadingPicture}
                                            className="absolute top-0 right-0 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                                            title="Delete picture"
                                        >
                                            <X size={16} />
                                        </button>
                                    </div>
                                ) : (
                                    <div className="w-32 h-32 rounded-full bg-slate-200 flex items-center justify-center border-4 border-slate-300">
                                        <User size={48} className="text-slate-400" />
                                    </div>
                                )}
                            </div>

                            <div className="flex-1 space-y-4">
                                <div>
                                    {profilePictureSource && (
                                        <p className={`text-xs mb-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                            Current: {profilePictureSource === 'linkedin' ? 'LinkedIn Profile Picture' : 'Uploaded Picture'}
                                        </p>
                                    )}
                                    <p className={`text-sm mb-3 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                                        Upload a profile picture (JPEG, PNG, GIF, or WebP). Maximum size: 5MB.
                                        Image will be automatically resized to 400x400 pixels.
                                    </p>
                                    <div className="flex gap-3 flex-wrap">
                                        <input
                                            type="file"
                                            ref={fileInputRef}
                                            onChange={handlePictureUpload}
                                            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                                            className="hidden"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => fileInputRef.current?.click()}
                                            disabled={uploadingPicture}
                                            className="flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <Upload size={18} />
                                            {uploadingPicture ? 'Uploading...' : profilePicture ? 'Change Picture' : 'Upload Picture'}
                                        </button>
                                        {profilePicture && (
                                            <button
                                                type="button"
                                                onClick={async () => {
                                                    try {
                                                        const preference = profilePictureSource === 'linkedin' ? 'uploaded' : 'linkedin';
                                                        await api.put('/user/profile-picture-preference', { preference });
                                                        const picResponse = await api.get(`/user/profile-picture?prefer_linkedin=${preference === 'linkedin'}`);
                                                        if (picResponse.data && picResponse.data.image) {
                                                            setProfilePicture(picResponse.data.image);
                                                            setProfilePictureSource(picResponse.data.source || preference);
                                                            setUser(prev => ({ ...prev, profilePicture: picResponse.data.image }));
                                                            setSuccess(`Switched to ${preference === 'linkedin' ? 'LinkedIn' : 'uploaded'} picture!`);
                                                        }
                                                    } catch (err) {
                                                        setError(err.response?.data?.error || 'Failed to switch picture source');
                                                    }
                                                }}
                                                className="flex items-center gap-2 px-6 py-3 border-2 border-primary-600 text-primary-600 rounded-xl font-semibold hover:bg-primary-50 transition-colors"
                                            >
                                                Use {profilePictureSource === 'linkedin' ? 'Uploaded' : 'LinkedIn'} Picture
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    <hr className={isDarkMode ? 'border-slate-700' : 'border-slate-100'} />

                    {/* Theme Preference */}
                    <section>
                        <div className="flex items-center gap-3 mb-6">
                            {isDarkMode ? <Moon className="text-indigo-600" size={24} /> : <Sun className="text-amber-600" size={24} />}
                            <h3 className={`text-xl font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Theme Preference</h3>
                        </div>
                        <div className="space-y-4">
                            <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Choose your preferred theme for the application.</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <label className={`flex items-center gap-3 p-4 border-2 rounded-2xl cursor-pointer transition-all ${
                                    !isDarkMode
                                        ? 'border-primary-500 bg-primary-50'
                                        : 'border-slate-600 hover:bg-slate-700'
                                }`}>
                                    <input
                                        type="radio"
                                        name="theme"
                                        value="light"
                                        checked={!isDarkMode}
                                        onChange={() => setDarkMode(false)}
                                        className="w-5 h-5 text-primary-600"
                                    />
                                    <div className="flex items-center gap-2">
                                        <Sun size={20} className={!isDarkMode ? 'text-primary-600' : 'text-slate-400'} />
                                        <span className={`font-medium ${
                                            !isDarkMode
                                                ? 'text-primary-900'
                                                : isDarkMode
                                                    ? 'text-slate-300'
                                                    : 'text-slate-700'
                                        }`}>Light Mode</span>
                                    </div>
                                </label>
                                <label className={`flex items-center gap-3 p-4 border-2 rounded-2xl cursor-pointer transition-all ${
                                    isDarkMode
                                        ? 'border-indigo-500 bg-indigo-900/30'
                                        : 'border-slate-200 hover:bg-slate-50'
                                }`}>
                                    <input
                                        type="radio"
                                        name="theme"
                                        value="dark"
                                        checked={isDarkMode}
                                        onChange={() => setDarkMode(true)}
                                        className="w-5 h-5 text-indigo-600"
                                    />
                                    <div className="flex items-center gap-2">
                                        <Moon size={20} className={isDarkMode ? 'text-indigo-400' : 'text-slate-400'} />
                                        <span className={`font-medium ${
                                            isDarkMode
                                                ? 'text-indigo-200'
                                                : 'text-slate-700'
                                        }`}>Dark Mode</span>
                                    </div>
                                </label>
                            </div>
                        </div>
                    </section>

                    {/* Messages */}
                    {error && (
                        <div className={`border px-4 py-3 rounded-lg ${
                            isDarkMode
                                ? 'bg-red-900/30 border-red-700 text-red-300'
                                : 'bg-red-50 border-red-200 text-red-700'
                        }`}>
                            {error}
                        </div>
                    )}
                    {success && (
                        <div className={`border px-4 py-3 rounded-lg ${
                            isDarkMode
                                ? 'bg-green-900/30 border-green-700 text-green-300'
                                : 'bg-green-50 border-green-200 text-green-700'
                        }`}>
                            {success}
                        </div>
                    )}

                    {/* Save */}
                    <div className="pt-4">
                        <button
                            type="submit"
                            disabled={saving}
                            className="flex items-center gap-2 px-8 py-4 bg-primary-600 text-white rounded-2xl font-bold hover:bg-primary-700 shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Save size={20} /> {saving ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
}
